import { useEffect, useRef } from 'preact/hooks';
import { type CubeLook, drawCubeIcon } from '../../render/icons';
import { prepareCanvas } from '../canvas';
import { prefersReducedMotion } from '../dom';

export interface CubePreviewProps {
  look: CubeLook;
  /** Edge length of the cube in CSS pixels. */
  size: number;
  /** Slow continuous rotation (skipped when the user prefers reduced motion). */
  spin?: boolean;
  glow?: boolean;
  /** Accessible description; without it the preview is decorative. */
  label?: string;
  class?: string;
}

/** Radians per second of the idle spin. */
const SPIN_SPEED = 0.9;

/** A cube icon on a small canvas, sharp on high-DPI screens. */
export function CubePreview({
  look,
  size,
  spin = false,
  glow = false,
  label,
  class: className,
}: CubePreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // A spinning cube needs room for its diagonal, the glow spreads ~0.25 of the size around it.
  const extent = spin ? Math.SQRT2 : 1;
  const margin = glow ? 0.3 : 0.04;
  const box = Math.ceil(size * (extent + 2 * margin));
  const { icon, primary, secondary } = look;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const draw = (rotation: number) => {
      const ctx = prepareCanvas(canvas, box, box);
      if (ctx)
        drawCubeIcon(ctx, { icon, primary, secondary }, box / 2, box / 2, size, { rotation, glow });
    };
    if (!spin || prefersReducedMotion()) {
      draw(0);
      return;
    }
    let frame = 0;
    const startedAt = performance.now();
    const loop = (now: number) => {
      draw(((now - startedAt) / 1000) * SPIN_SPEED);
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [icon, primary, secondary, size, box, spin, glow]);

  return (
    <canvas
      ref={canvasRef}
      class={className}
      width={box}
      height={box}
      style={{ width: `${box}px`, height: `${box}px` }}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : 'true'}
    />
  );
}
